import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { isPushSupported, subscribeUserToPush } from "@/utils/push";
import { PUSH } from "@/constants/testIds";

const DISMISS_KEY = "ams_push_dismissed_until";

export default function PushNotificationPrompt() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user || !isPushSupported()) return;

    if (Notification.permission === "granted") {
      subscribeUserToPush().catch(() => {});
      return;
    }

    if (Notification.permission === "denied") return;

    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedUntil > Date.now()) return;

    const timer = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(timer);
  }, [user]);

  const handleEnable = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        await subscribeUserToPush();
      }
    } catch (e) {
      /* silent */
    } finally {
      setVisible(false);
    }
  };

  const handleLater = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid={PUSH.prompt}
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-xl sm:left-auto sm:right-4"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Bell className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold">{t("push.promptTitle")}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t("push.promptDesc")}</p>
        <div className="mt-3 flex gap-2">
          <Button size="sm" className="rounded-full" data-testid={PUSH.enableBtn} onClick={handleEnable}>
            {t("push.enable")}
          </Button>
          <Button size="sm" variant="ghost" className="rounded-full" data-testid={PUSH.laterBtn} onClick={handleLater}>
            {t("push.later")}
          </Button>
        </div>
      </div>
      <button className="text-muted-foreground hover:text-foreground" onClick={handleLater} aria-label="close">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
